import { useEffect, useState } from "react";
import {
  CoverPage,
  PdfFilterModal,
  EymemoryTimelinePagesWrapper,
} from "./components";
import { CustomButton } from "src/app/components";
import { PiPrinterFill } from "react-icons/pi";
import { format, formatISO, parse, parseISO } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { getCurrentChild } from "src/shared/redux-state/src";
import { useAppSelector } from "src/app/services/store";
import { ChildrenStoreData } from "src/shared/types/src";
import { FilterIcon } from "src/app/static/images/svg";

const PdfBookContainer = () => {
  const [isPdfFilterModal, setIsPdfFilterModal] = useState(false);
  const [dob, setDob] = useState<string>();
  const [isPrintAllowed, setIsPrintAllowed] = useState(false);
  const [isDataPresent, setIsDataPresent] = useState(false);
  const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

  const [searchParams, setSearchParams] = useSearchParams();

  const getDateFromParam = (key: string): Date | undefined => {
    const value = searchParams.get(key);
    return value ? parse(value, "yyyy-MM-dd", new Date()) : undefined;
  };

  const [filters, setFilters] = useState(() => ({
    fromDate: getDateFromParam("from"),
    toDate: getDateFromParam("to"),
  }));

  const togglePdfFilterModal = () => {
    setIsPdfFilterModal(!isPdfFilterModal);
  };

  useEffect(() => {
    document.title = `My Learning Journey ${
      currentChild?.name ? `- ${currentChild.name}` : ""
    }`;
  }, []);

  const handleApplyFilter = (fromDate?: Date, toDate?: Date) => {
    const updatedParams: any = {};

    if (fromDate)
      updatedParams.from = formatISO(fromDate, {
        representation: "date",
      });
    else searchParams.delete("from");

    if (toDate)
      updatedParams.to = formatISO(toDate, {
        representation: "date",
      });
    else searchParams.delete("to");

    setSearchParams(updatedParams);
    setFilters({ fromDate, toDate });
    setIsPdfFilterModal(false);
  }

  return (
    <div className={`main-wrapper bg-white container pd-t-50 pd-b-20`}>
      <div className={`header py-0 pdf-book-header`}>
        <div className="container h-100">
          <div className="d-flex align-items-center justify-content-end h-100">
            <div className="d-flex align-items-center">
              <div className="d-flex position-relative cursor-pointer">
                <span onClick={togglePdfFilterModal}>
                  <FilterIcon />
                </span>
                {filters?.fromDate || filters?.toDate ? (
                  <span className="filter-badge"></span>
                ) : null}
              </div>
              {isPdfFilterModal && (
                <PdfFilterModal
                  filters={{
                    fromDate: filters?.fromDate,
                    toDate: filters?.toDate,
                  }}
                  applyFilter={handleApplyFilter}
                  isPdfFilterModal={isPdfFilterModal}
                  togglePdfFilterModal={togglePdfFilterModal}
                />
              )}
              <CustomButton
                icon={<PiPrinterFill size={20} />}
                color="link"
                customBtnStyle="custom-btn-icon wh-30 justify-content-center ms-2"
                disabled={!isPrintAllowed || !isDataPresent}
                onClick={() => {
                  setTimeout(() => window.print(), 200);
                }}
                style={{pointerEvents: 'visible', opacity: isPrintAllowed && isDataPresent ? 1 :  0.7}}
                title={
                  isPrintAllowed
                    ? `Print “My Learning Journey”`
                    : "No observation to print"
                }
              />
            </div>
          </div>
        </div>
      </div>

      <CoverPage
        childName={currentChild?.name || ""}
        date={dob || ""}
        profilePicture={currentChild?.photo}
        nurseryName={currentChild?.nursery?.name || ""}
        nurseryLogo={currentChild?.nursery?.logo}
      />
      {/* Loader starts */}
      <div
        className={`full-screen-loader full-screen-cover-loader ${
          isDataPresent ? "d-none" : ""
        }`}
      >
        <div className="d-flex flex-column align-items-center justify-content-center full-screen-inner-cover">
          <Spinner size={"lg"} />
          <p className="text-center mg-t-20 ">
            Please wait while we load all observation images for a complete
            print preview. This may take a moment.
          </p>
          <p className="text-center mg-t-10 ">
            Once loading is complete, click the{" "}
            <strong>Print icon in the header</strong> to download or save the
            file.
          </p>
        </div>
      </div>
      {/* Loader ends */}
      <div className={`${!isDataPresent ? "d-none" : ""}`}>
        <EymemoryTimelinePagesWrapper
          filters={filters}
          isDataLoaded={(value: boolean) => {
            setIsDataPresent(value);
          }}
          onError={() => {
            setIsDataPresent(false);
            setIsPrintAllowed(false)
          }}
          child={currentChild}
          onDobFetch={(dob: string) => {
            setDob(dob ? format(parseISO(dob), "d MMMM yyyy") : "");
          }}
          isObservationAvailable={(available) => {
            setIsPrintAllowed(available);
          }}
        />
      </div>
    </div>
  );
};

export default PdfBookContainer;
